import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Typography,
  Row,
  Col,
  Radio,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './index.less';

const { Title, Text } = Typography;
const { TextArea } = Input;

const BASE_URL = 'https://ds2.cn/dmo';

interface DeepMonsterType {
  id: number;
  code: string;
  name: string;
  description: string;
  level: string;
  basePower: number;
  baseDefense: number;
  baseSpeed: number;
  created: string;
  updated: string;
  instanceCount?: number;
}

interface DeepMonsterInstance {
  id: number;
  hash: string;
  currentLevel: number;
  status: string;
  created: string;
}

const DeepMonstersAdmin: React.FC = () => {
  const [types, setTypes] = useState<DeepMonsterType[]>([]);
  const [loading, setLoading] = useState(false);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [batchGenerateModalVisible, setBatchGenerateModalVisible] = useState(false);
  const [batchUploadModalVisible, setBatchUploadModalVisible] = useState(false);
  const [selectedType, setSelectedType] = useState<DeepMonsterType | null>(null);
  const [uploadedData, setUploadedData] = useState<any[]>([]);

  // 批量生成进度状态
  const [batchProgress, setBatchProgress] = useState({
    currentType: '',
    currentTypeIndex: 0,
    totalTypes: 0,
    status: 'idle' as 'idle' | 'running' | 'completed' | 'error',
    completedTypes: [] as string[],
    failedTypes: [] as { code: string; error: string }[]
  });

  const [typeForm] = Form.useForm();
  const [generateForm] = Form.useForm();
  const [batchGenerateForm] = Form.useForm();

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/deep-monsters/types');
      const data = await response.json();
      if (data.success) {
        setTypes(data.data || []);
      } else {
        message.error(data.message || '获取深海巨兽种类失败');
      }
    } catch (error) {
      message.error('获取深海巨兽种类失败');
    } finally {
      setLoading(false);
    }
  };

  const createType = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/deep-monsters/types', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: values.code,
          name: values.name,
          description: values.description,
          level: values.level || 'SR',
          basePower: values.basePower || 0,
          baseDefense: values.baseDefense || 0,
          baseSpeed: values.baseSpeed || 0
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success('创建深海巨兽种类成功');
        setTypeModalVisible(false);
        typeForm.resetFields();
        fetchTypes();
      } else {
        message.error(data.message || '创建深海巨兽种类失败');
      }
    } catch (error) {
      message.error('创建深海巨兽种类失败');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    // 创建模板数据
    const templateData = [
      {
        '编号': '001',
        '名称': '深渊巨鲨',
        '描述': '栖息在深渊中的巨大鲨鱼，拥有锋利的牙齿和强大的力量',
        '稀有度': 'SR',
        '基础力量': 100,
        '基础防御': 80,
        '基础速度': 60
      },
      {
        '编号': '002',
        '名称': '电鳗王',
        '描述': '能够释放强大电流的巨型电鳗',
        '稀有度': 'SSR',
        '基础力量': 120,
        '基础防御': 70,
        '基础速度': 90
      }
    ];

    // 创建工作簿
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '深海巨兽模板');

    // 下载文件
    XLSX.writeFile(wb, '深海巨兽批量导入模板.xlsx');
    message.success('模板下载成功');
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // 验证并转换数据
        const validData = jsonData.map((row: any) => ({
          code: row['编号'] || row['code'],
          name: row['名称'] || row['name'],
          description: row['描述'] || row['description'],
          level: row['稀有度'] || row['level'] || 'SR',
          basePower: Number(row['基础力量'] || row['basePower'] || 0),
          baseDefense: Number(row['基础防御'] || row['baseDefense'] || 0),
          baseSpeed: Number(row['基础速度'] || row['baseSpeed'] || 0)
        }));

        setUploadedData(validData);
        message.success(`成功读取 ${validData.length} 条数据`);
      } catch (error) {
        message.error('文件解析失败，请检查文件格式');
      }
    };
    reader.readAsBinaryString(file);
    return false; // 阻止自动上传
  };

  const batchCreateTypes = async () => {
    if (uploadedData.length === 0) {
      message.warning('请先上传文件');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const item of uploadedData) {
      try {
        const response = await fetch('/api/v1/deep-monsters/types', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(item)
        });

        const data = await response.json();
        if (data.success) {
          successCount++;
        } else {
          failCount++;
          console.error(`创建失败: ${item.name}`, data.message);
        }
      } catch (error) {
        failCount++;
        console.error(`创建失败: ${item.name}`, error);
      }
    }

    setLoading(false);
    message.success(`批量创建完成！成功: ${successCount}, 失败: ${failCount}`);
    setBatchUploadModalVisible(false);
    setUploadedData([]);
    fetchTypes();
  };

  const generateInstances = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/deep-monsters/types/${selectedType?.id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity: values.quantity
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success(`成功生成 ${values.quantity} 个实例`);

        // 生成下载内容
        const instances = data.data.instances as DeepMonsterInstance[];
        const content = instances.map(i => `${BASE_URL}/${i.hash}`).join('\r\n') + '\r\n';
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
        const fileName = `${selectedType?.code}.txt`;
        saveAs(blob, fileName);

        setGenerateModalVisible(false);
        generateForm.resetFields();
        fetchTypes();
      } else {
        message.error(data.message || '生成实例失败');
      }
    } catch (error) {
      message.error('生成实例失败');
    } finally {
      setLoading(false);
    }
  };

  // 带重试的请求函数
  const fetchWithRetry = async (
    url: string,
    options: RequestInit,
    maxRetries = 3,
    retryDelay = 1000
  ): Promise<Response> => {
    let lastError: Error | null = null;

    for (let i = 0; i <= maxRetries; i++) {
      try {
        const response = await fetch(url, options);

        if (response.ok) {
          return response;
        }

        if (response.status >= 400 && response.status < 500) {
          return response;
        }

        throw new Error(`Server error: ${response.status}`);
      } catch (error: any) {
        lastError = error;

        if (i === maxRetries) {
          break;
        }

        const delay = retryDelay * Math.pow(2, i);
        console.log(`请求失败，${delay}ms 后重试 (${i + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError || new Error('请求失败');
  };

  // ZIP打包下载
  const downloadAsZip = async (files: { code: string; content: string }[]) => {
    try {
      setLoading(true);

      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      files.forEach(file => {
        zip.file(`${file.code}.txt`, `\ufeff${file.content}`, { binary: false });
      });

      const content = await zip.generateAsync({ type: 'blob' });
      const fileName = `deep_monsters_batch_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.zip`;
      saveAs(content, fileName);

      message.success(`成功生成ZIP文件包含 ${files.length} 个文件`);
    } catch (error) {
      message.error('生成ZIP文件失败');
      console.error('ZIP generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 批量生成实例
  const batchGenerateInstances = async (values: any) => {
    const { quantity, downloadType } = values;

    setBatchProgress({
      currentType: '',
      currentTypeIndex: 0,
      totalTypes: types.length,
      status: 'running',
      completedTypes: [],
      failedTypes: []
    });

    setLoading(true);

    try {
      const allFileData: { code: string; content: string }[] = [];

      for (let i = 0; i < types.length; i++) {
        const type = types[i];

        setBatchProgress(prev => ({
          ...prev,
          currentType: type.code,
          currentTypeIndex: i + 1
        }));

        try {
          const response = await fetchWithRetry(
            `/api/v1/deep-monsters/types/${type.id}/generate`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ quantity })
            },
            3,
            1000
          );

          const data = await response.json();

          if (data.success && data.data && data.data.instances) {
            const content = data.data.instances
              .map((instance: any) => `${BASE_URL}/${instance.hash}`)
              .join('\r\n') + '\r\n';

            allFileData.push({ code: type.code, content });

            setBatchProgress(prev => ({
              ...prev,
              completedTypes: [...prev.completedTypes, type.code]
            }));

            if (downloadType === 'batch') {
              try {
                const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
                const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
                const fileName = `${type.code}.txt`;
                saveAs(blob, fileName);

                await new Promise(resolve => setTimeout(resolve, 500));
              } catch (saveError) {
                console.error(`保存文件 ${type.code} 失败:`, saveError);
              }
            }
          } else {
            throw new Error(data.message || '生成失败');
          }
        } catch (error: any) {
          const errorMsg = error.message || '未知错误';
          console.error(`生成 ${type.code} 失败:`, error);
          setBatchProgress(prev => ({
            ...prev,
            failedTypes: [...prev.failedTypes, { code: type.code, error: errorMsg }]
          }));
        }
      }

      if (downloadType === 'zip' && allFileData.length > 0) {
        try {
          await downloadAsZip(allFileData);
        } catch (zipError) {
          console.error('ZIP打包失败:', zipError);
          message.error('ZIP打包失败，但数据已生成');
        }
      }

      setBatchProgress(prev => ({ ...prev, status: 'completed' }));

      const successCount = allFileData.length;
      const failCount = types.length - successCount;

      if (failCount > 0) {
        message.warning(`批量生成完成：成功 ${successCount} 个，失败 ${failCount} 个`);
      } else {
        message.success(`批量生成成功：共 ${successCount} 个文件`);
      }

      fetchTypes();

      setTimeout(() => {
        if (batchProgress.status === 'completed') {
          setBatchGenerateModalVisible(false);
          batchGenerateForm.resetFields();
          setBatchProgress({
            currentType: '',
            currentTypeIndex: 0,
            totalTypes: 0,
            status: 'idle',
            completedTypes: [],
            failedTypes: []
          });
        }
      }, 2000);

    } catch (error) {
      console.error('批量生成失败:', error);
      message.error('批量生成失败');
      setBatchProgress(prev => ({ ...prev, status: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const typeColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '编号',
      dataIndex: 'code',
      key: 'code',
      width: 100,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '稀有度',
      dataIndex: 'level',
      key: 'level',
      width: 80,
    },
    {
      title: '基础力量',
      dataIndex: 'basePower',
      key: 'basePower',
      width: 100,
    },
    {
      title: '基础防御',
      dataIndex: 'baseDefense',
      key: 'baseDefense',
      width: 100,
    },
    {
      title: '基础速度',
      dataIndex: 'baseSpeed',
      key: 'baseSpeed',
      width: 100,
    },
    {
      title: '实例数量',
      dataIndex: 'instanceCount',
      key: 'instanceCount',
      width: 100,
      render: (count: number) => count || 0,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: DeepMonsterType) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedType(record);
              setGenerateModalVisible(true);
            }}
          >
            生成实例
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="deep-monsters-admin">
      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>深海巨兽管理</Title>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setTypeModalVisible(true)}
            >
              创建新种类
            </Button>
            <Button
              icon={<UploadOutlined />}
              onClick={() => setBatchUploadModalVisible(true)}
            >
              批量导入
            </Button>
            <Button
              onClick={() => setBatchGenerateModalVisible(true)}
            >
              批量生成
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
            >
              下载模板
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTypes}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          columns={typeColumns}
          dataSource={types}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 创建种类 Modal */}
      <Modal
        title="创建深海巨兽种类"
        open={typeModalVisible}
        onOk={() => typeForm.submit()}
        onCancel={() => {
          setTypeModalVisible(false);
          typeForm.resetFields();
        }}
        width={600}
      >
        <Form
          form={typeForm}
          layout="vertical"
          onFinish={createType}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="编号"
                rules={[{ required: true, message: '请输入编号' }]}
              >
                <Input placeholder="例如: 001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="名称"
                rules={[{ required: true, message: '请输入名称' }]}
              >
                <Input placeholder="例如: 深渊巨鲨" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <TextArea rows={3} placeholder="描述这只深海巨兽的特征和故事..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="level"
                label="稀有度"
                initialValue="SR"
                rules={[{ required: true, message: '请选择稀有度' }]}
              >
                <Select style={{ width: '100%' }}>
                  <Select.Option value="UR">UR</Select.Option>
                  <Select.Option value="QR">QR</Select.Option>
                  <Select.Option value="SSR">SSR</Select.Option>
                  <Select.Option value="SR">SR</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="basePower"
                label="基础力量"
                initialValue={0}
                rules={[{ required: true, message: '请输入基础力量' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="baseDefense"
                label="基础防御"
                initialValue={0}
                rules={[{ required: true, message: '请输入基础防御' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="baseSpeed"
                label="基础速度"
                initialValue={0}
                rules={[{ required: true, message: '请输入基础速度' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 生成实例 Modal */}
      <Modal
        title={`生成实例 - ${selectedType?.name}`}
        open={generateModalVisible}
        onOk={() => generateForm.submit()}
        onCancel={() => {
          setGenerateModalVisible(false);
          generateForm.resetFields();
        }}
      >
        <Form
          form={generateForm}
          layout="vertical"
          onFinish={generateInstances}
        >
          <Form.Item
            name="quantity"
            label="生成数量"
            initialValue={100}
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber min={1} max={1000} style={{ width: '100%' }} />
          </Form.Item>
          <Text type="secondary">
            生成后会自动下载包含所有实例链接的文本文件
          </Text>
        </Form>
      </Modal>

      {/* 批量导入 Modal */}
      <Modal
        title="批量导入深海巨兽种类"
        open={batchUploadModalVisible}
        onOk={batchCreateTypes}
        onCancel={() => {
          setBatchUploadModalVisible(false);
          setUploadedData([]);
        }}
        width={800}
        okText="确认导入"
        cancelText="取消"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text>请上传Excel文件（.xlsx或.xls格式）</Text>
            <br />
            <Text type="secondary">
              Excel表格应包含以下列：编号、名称、描述、稀有度、基础力量、基础防御、基础速度
            </Text>
          </div>

          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileUpload(file);
              }
            }}
            style={{
              padding: '8px',
              border: '1px dashed #d9d9d9',
              borderRadius: '4px',
              width: '100%',
              cursor: 'pointer'
            }}
          />

          {uploadedData.length > 0 && (
            <div>
              <Text strong>已读取 {uploadedData.length} 条数据：</Text>
              <Table
                dataSource={uploadedData}
                columns={[
                  { title: '编号', dataIndex: 'code', key: 'code', width: 80 },
                  { title: '名称', dataIndex: 'name', key: 'name', width: 120 },
                  { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
                  { title: '稀有度', dataIndex: 'level', key: 'level', width: 80 },
                  { title: '力量', dataIndex: 'basePower', key: 'basePower', width: 70 },
                  { title: '防御', dataIndex: 'baseDefense', key: 'baseDefense', width: 70 },
                  { title: '速度', dataIndex: 'baseSpeed', key: 'baseSpeed', width: 70 },
                ]}
                pagination={false}
                size="small"
                scroll={{ y: 300 }}
                rowKey={(record, index) => index?.toString() || '0'}
              />
            </div>
          )}
        </Space>
      </Modal>

      {/* 批量生成 Modal */}
      <Modal
        title="批量生成深海巨兽实例"
        open={batchGenerateModalVisible}
        onCancel={() => {
          if (loading && batchProgress.status === 'running') {
            Modal.confirm({
              title: '确认取消',
              content: '批量生成正在进行中，确定要取消吗？',
              onOk: () => {
                setBatchGenerateModalVisible(false);
                batchGenerateForm.resetFields();
                setBatchProgress(prev => ({ ...prev, status: 'idle' }));
              }
            });
          } else {
            setBatchGenerateModalVisible(false);
            batchGenerateForm.resetFields();
          }
        }}
        footer={null}
        width={600}
      >
        <Form
          form={batchGenerateForm}
          layout="vertical"
          onFinish={batchGenerateInstances}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              将为所有深海巨兽种类（共 {types.length} 个）各生成指定数量的实例，
              逐个类型依次生成和下载，避免一次性加载大量数据导致浏览器卡顿。
            </Text>
          </div>

          <Form.Item
            name="quantity"
            label="每种深海巨兽生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber
              min={1}
              max={10000}
              placeholder="请输入每种深海巨兽生成的数量"
              style={{ width: '100%' }}
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="downloadType"
            label="下载方式"
            initialValue="batch"
            rules={[{ required: true, message: '请选择下载方式' }]}
          >
            <Radio.Group disabled={loading}>
              <Radio value="batch">即时下载（推荐）</Radio>
              <Radio value="zip">ZIP打包下载</Radio>
            </Radio.Group>
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              • 即时下载：每生成一个类型立即下载，内存占用小<br />
              • ZIP打包：生成完所有数据后打包成一个ZIP文件，数据量大时内存占用较高
            </Text>
          </div>

          {/* 批量生成进度显示 */}
          {batchProgress.status !== 'idle' && (
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong>生成进度</Text>
              </div>

              <div style={{ marginBottom: 8 }}>
                <Text>
                  {batchProgress.status === 'running' && `正在处理: ${batchProgress.currentType}`}
                  {batchProgress.status === 'completed' && '✓ 批量生成已完成'}
                  {batchProgress.status === 'error' && '✗ 批量生成出错'}
                </Text>
              </div>

              <Progress
                percent={batchProgress.totalTypes > 0
                  ? Math.round((batchProgress.currentTypeIndex / batchProgress.totalTypes) * 100)
                  : 0}
                status={
                  batchProgress.status === 'completed' ? 'success' :
                  batchProgress.status === 'error' ? 'exception' :
                  'active'
                }
                format={() => `${batchProgress.currentTypeIndex}/${batchProgress.totalTypes}`}
              />

              <div style={{ marginTop: 12, fontSize: '12px' }}>
                <Text type="success">已完成: {batchProgress.completedTypes.length} 个</Text>
                {batchProgress.failedTypes.length > 0 && (
                  <>
                    <br />
                    <Text type="danger">失败: {batchProgress.failedTypes.length} 个</Text>
                    <div style={{ marginTop: 8, maxHeight: 100, overflow: 'auto' }}>
                      {batchProgress.failedTypes.map((item, idx) => (
                        <div key={idx} style={{ color: '#ff4d4f' }}>
                          • {item.code}: {item.error}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={batchProgress.status === 'running'}
                icon={loading ? undefined : <DownloadOutlined />}
              >
                {loading ? '生成中...' : '开始批量生成'}
              </Button>

              <Button
                onClick={() => {
                  if (loading && batchProgress.status === 'running') {
                    Modal.confirm({
                      title: '确认取消',
                      content: '批量生成正在进行中，确定要关闭吗？已生成的数据不会丢失。',
                      onOk: () => {
                        setBatchGenerateModalVisible(false);
                        batchGenerateForm.resetFields();
                      }
                    });
                  } else {
                    setBatchGenerateModalVisible(false);
                    batchGenerateForm.resetFields();
                    if (batchProgress.status !== 'idle') {
                      setBatchProgress({
                        currentType: '',
                        currentTypeIndex: 0,
                        totalTypes: 0,
                        status: 'idle',
                        completedTypes: [],
                        failedTypes: []
                      });
                    }
                  }
                }}
                disabled={loading && batchProgress.status === 'running'}
              >
                关闭
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeepMonstersAdmin;
